import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Per-route document title + meta description + canonical + OG URL.
 *
 * React Helmet would be the canonical choice but adds 14 KB and a provider
 * to wrap every page. This hook is 80 lines and does the things we need:
 * keep <title>, <meta name="description">, <link rel="canonical">,
 * <meta property="og:url">, <meta property="og:title">,
 * <meta property="og:description"> in sync with the current route. Falls
 * back to the defaults for any unmapped path.
 *
 * NOINDEX policy: /admin/**, /discover, /insights-preview/** and any
 * /thank-you/** route get robots=noindex,nofollow,noarchive. Everything
 * else stays index/follow (the site-wide raw-HTML robots meta from the
 * pre-render step is already index/follow).
 */
const SITE = '';
const TITLE_PREFIX = 'TravelTrucks — ';
const DEFAULT_TITLE = 'TravelTrucks OS';
const DEFAULT_DESC = 'TravelTrucks Company OS — Base Camp. Quotes, fitting-bay jobs, customers and the day\'s work in one place.';
const DEFAULT_OG_IMAGE = `${SITE}/brand/traveltrucks-icon.svg`;

// Map: { route → { title, description, ogTitle?, ogDescription?, ogImage? } }.
// If ogTitle/description aren't set, we fall back to title/description.
const ROUTE_META = {
  '/':                       { title: DEFAULT_TITLE, description: DEFAULT_DESC },
  '/what-we-fix':            { title: TITLE_PREFIX + 'What we fix', description: 'Five places your business quietly leaks money — time, money, lost work, pricing, and blind spots. In plain English.' },
  '/what-we-build':          { title: TITLE_PREFIX + 'What we build', description: 'Apps, systems, automation and AI under one roof. Fixed-price, no lock-in.' },
  '/ai':                     { title: TITLE_PREFIX + 'AI', description: "The boring half of your business, handled. Real AI use-cases for Aussie SMBs — no buzzwords." },
  '/case-studies':           { title: TITLE_PREFIX + 'Case studies', description: 'Real businesses, real leaks, real money found. Worked examples in plain English.' },
  '/how-it-works':           { title: TITLE_PREFIX + 'How it works', description: 'Four clear steps from "something\'s not adding up" to "sorted." Calm, plain-English process.' },
  '/leak-check':             { title: TITLE_PREFIX + 'The Blueprint · $1,950 · 2 weeks', description: 'A 2-week diagnostic that finds the leaks draining your business and gives you a Scorecard, Report and Action Plan in plain English. $1,950 AUD fixed-price, no lock-in.' },
  '/admin-tax-calculator':   { title: TITLE_PREFIX + 'Calculate your time leak', description: 'How much is your admin time really costing you each year? Quick, honest calculator. No sales follow-up.' },
  '/services':               { title: TITLE_PREFIX + 'Services', description: 'The Blueprint, Implementation packages, and the Clarity Partner. Plain pricing, fixed scope, no lock-in.' },
  '/results':                { title: TITLE_PREFIX + 'Results', description: 'Worked examples — based on real patterns we see in Aussie SMBs. Real client case studies land as our beta delivers.' },
  '/about':                  { title: TITLE_PREFIX + 'About', description: '22 years in IT, networks and systems. Real businesses, real experience. Built for Aussie SMBs by an Aussie SMB owner.' },
  '/implementation':         { title: TITLE_PREFIX + 'Implementation', description: 'Fixed-price, fixed-scope implementation of the Action Plan from your Blueprint. Paid 50/50 deposit + completion.' },
  '/clarity-partner':        { title: TITLE_PREFIX + 'Clarity Partner', description: 'A monthly rhythm of small, well-chosen moves that keep your bucket plugged. No lock-in; cancel with 30 days.' },
  '/fractional-ops':         { title: TITLE_PREFIX + 'Fractional Ops', description: 'Done-with-you ops for businesses past the Implementation phase but not yet ready for a full-time COO.' },
  '/contact':                { title: TITLE_PREFIX + 'Contact', description: 'Get in touch with TravelTrucks. Real reply within one working day.' },
  '/guide':                  { title: TITLE_PREFIX + 'The Five Leaks guide', description: 'A plain-English guide to the five leaks draining Aussie SMBs. Free; emailed to you.' },
  '/clarity':                { title: TITLE_PREFIX + 'Find your biggest leak in 90 seconds', description: 'The Clarity walk-through — calm, plain English, and built around your business.' },
  '/clarity-qr':             { title: TITLE_PREFIX + 'Free Leak Check', description: "What's your business really losing? Find out in 30 minutes." },
  '/book':                   { title: TITLE_PREFIX + 'Book a free 30-min Leak Check', description: 'Lock in your free 30-minute Leak Check call with Michael. No pitch. No jargon.' },
  '/thank-you/booking':      { title: TITLE_PREFIX + 'Your Leak Check is booked', description: 'Confirmation page for your Leak Check booking. Next step: complete the intake.' },
  '/thank-you/guide':        { title: TITLE_PREFIX + 'Guide on its way', description: 'Your Five Leaks guide is on its way to your inbox.' },
  '/privacy':                { title: TITLE_PREFIX + 'Privacy', description: 'How TravelTrucks collects, stores and uses your data. Plain English. Australian-owned, Brendale QLD.' },
  '/terms':                  { title: TITLE_PREFIX + 'Terms of engagement', description: 'The terms of working with TravelTrucks. Fixed-price, no lock-in, 30-day cancellation.' },
  '/from-michael':           {
    title: TITLE_PREFIX + "From Michael — a plain-English catch-up",
    description: "Michael's back — same voice, sharper tools. A warm hello from the founder behind Less Stress IT, ProCloud Creative and Rise SEO — and where he's taking it next.",
    ogTitle: "From Michael — a plain-English catch-up",
    ogDescription: "Michael's back — same voice, sharper tools. A warm hello from the founder behind Less Stress IT, ProCloud Creative and Rise SEO — and where he's taking it next.",
  },
  '/sample-report':          {
    title: TITLE_PREFIX + 'See a real Blueprint',
    description: "A worked sample of the Blueprint — anonymised trades business, real structure, real numbers. See what you'd get before you book.",
    ogTitle: 'See a real TravelTrucks Blueprint',
    ogDescription: "A worked sample of the TravelTrucks Blueprint — anonymised trades business, real structure, real numbers. See what you'd get before you book.",
  },

  // Legacy /v2/* aliases — same titles as the bare-domain routes above so any
  // in-flight QR codes / marketing links that still point at /v2/… inherit
  // the live titles + descriptions (no "staging" leftovers).
  '/v2':                     { title: DEFAULT_TITLE, description: DEFAULT_DESC },
  '/v2/what-we-fix':         { title: TITLE_PREFIX + 'What we fix', description: 'Five places your business quietly leaks money — time, money, lost work, pricing, and blind spots. In plain English.' },
  '/v2/what-we-build':       { title: TITLE_PREFIX + 'What we build', description: 'Apps, systems, automation and AI under one roof. Fixed-price, no lock-in.' },
  '/v2/ai':                  { title: TITLE_PREFIX + 'AI', description: "The boring half of your business, handled. Real AI use-cases for Aussie SMBs — no buzzwords." },
  '/v2/case-studies':        { title: TITLE_PREFIX + 'Case studies', description: 'Real businesses, real leaks, real money found. Worked examples in plain English.' },
  '/v2/how-it-works':        { title: TITLE_PREFIX + 'How it works', description: 'Four clear steps from "something\'s not adding up" to "sorted." Calm, plain-English process.' },
  '/v2/about':               { title: TITLE_PREFIX + 'About', description: '22 years in IT, networks and systems. Real businesses, real experience. Built for Aussie SMBs by an Aussie SMB owner.' },
  '/v2/clarity-qr':          { title: TITLE_PREFIX + 'Free Leak Check', description: "What's your business really losing? Find out in 30 minutes." },
  // /discover — TravelTrucks V1 Discovery flow. Live-but-unlisted, noindex.
  '/discover':               { title: TITLE_PREFIX + 'A quick chat', description: 'A short conversation that helps us spot where your business is quietly leaking time or money.' },
};

function setMeta(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setMetaProperty(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('property', property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function isNoindexPath(pathname) {
  if (!pathname) return false;
  if (pathname === '/discover' || pathname.startsWith('/discover/')) return true;
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return true;
  if (pathname.startsWith('/insights-preview')) return true;
  if (pathname.startsWith('/quote/')) return true; // client-token pages, private
  if (pathname.startsWith('/leak/')) return true;  // private client teaser
  if (pathname.startsWith('/demo/')) return true;  // private demo pages
  if (pathname === '/thank-you/booking' || pathname === '/thank-you/guide') return true;
  return false;
}

export default function DocumentHead() {
  const { pathname } = useLocation();
  useEffect(() => {
    const meta = ROUTE_META[pathname] || { title: DEFAULT_TITLE, description: DEFAULT_DESC };
    document.title = meta.title;
    setMeta('description', meta.description);
    // canonical + og — normalise trailing slash except root.
    const clean = pathname === '/' ? '/' : pathname.replace(/\/+$/, '');
    const canonical = `${SITE}${clean}`;
    setCanonical(canonical);
    setMetaProperty('og:url', canonical);
    setMetaProperty('og:title', meta.ogTitle || meta.title);
    setMetaProperty('og:description', meta.ogDescription || meta.description);
    setMetaProperty('og:image', meta.ogImage || DEFAULT_OG_IMAGE);
    // Twitter mirrors OG.
    setMeta('twitter:title', meta.ogTitle || meta.title);
    setMeta('twitter:description', meta.ogDescription || meta.description);
    setMeta('twitter:image', meta.ogImage || DEFAULT_OG_IMAGE);

    // robots — clear any lingering value, then set noindex on protected routes.
    const existing = document.querySelector('meta[name="robots"]');
    if (existing) existing.parentNode.removeChild(existing);
    if (isNoindexPath(pathname)) {
      setMeta('robots', 'noindex, nofollow, noarchive');
    }
  }, [pathname]);
  return null;
}
